import { useEffect, useState } from "react";
import { getTrainingEvalRunSummariesBatched, type TrainingEvalRunSummaryView } from "@/api";
import { type LoadState, resolveLoadErrorMessage } from "@/components/dataset";

/**
 * 평가 run 요약 배치 로더. 체크포인트↔평가 join(epoch·budget_unit·success_rate)의 소스다.
 *
 * 청크 분할은 엔드포인트 계약이라 `getTrainingEvalRunSummariesBatched` 가 소유한다. 어느 청크든
 * 실패하면 전체를 오류로 둔다. 그러면 join 컬럼은 "—" 로 표시한다.
 */
export function useSessionEvalRunSummaries(
  evalRunIds: readonly string[],
  options: { enabled: boolean },
): { state: LoadState<TrainingEvalRunSummaryView[]> } {
  const { enabled } = options;
  const [state, setState] = useState<LoadState<TrainingEvalRunSummaryView[]>>({
    status: "idle",
  });
  const idsKey = evalRunIds.join("\n");

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const ids = idsKey === "" ? [] : idsKey.split("\n");
    if (ids.length === 0) {
      setState({ status: "success", data: [] });
      return;
    }
    let cancelled = false;
    setState((prev) => (prev.status === "success" ? prev : { status: "loading" }));
    getTrainingEvalRunSummariesBatched(ids).then(
      (summaries) => {
        if (!cancelled) {
          setState({ status: "success", data: summaries });
        }
      },
      (error: unknown) => {
        if (!cancelled) {
          setState({ status: "error", message: resolveLoadErrorMessage(error) });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [idsKey, enabled]);

  return { state };
}
